import React, { useEffect, useState } from "react";
import { Mail, Save, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import RichEditor from "../../components/RichEditor";
import { sanitizeHtml } from "../../lib/sanitize";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";

export default function AdminEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ subject: "", body_html: "", enabled: true });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  const load = () => api.get("/admin/email-templates").then((r) => {
    setTemplates(r.data);
    if (!active && r.data.length) selectTpl(r.data[0]);
  }).catch(() => toast.error("Could not load templates"));
  useEffect(() => { load(); }, []);

  const selectTpl = (t) => {
    setActive(t.name);
    setForm({ subject: t.subject, body_html: t.body_html, enabled: t.enabled !== false });
    setPreview(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/email-templates/${active}`, form);
      toast.success("Template saved");
      load();
    } catch (e) { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const current = templates.find((t) => t.name === active);

  return (
    <div data-testid="admin-email-templates">
      <h1 className="font-display text-4xl font-semibold text-primary mb-1">Email templates</h1>
      <p className="text-muted-foreground mb-8">Edit the subject and body of every automated email. Use placeholders like <code className="text-secondary">{"{{name}}"}</code>.</p>
      <div className="grid lg:grid-cols-[220px_1fr] gap-8">
        <div className="space-y-1">
          {templates.map((t) => (
            <button key={t.name} data-testid={`email-tpl-${t.name}`} onClick={() => selectTpl(t)}
              className={`w-full text-left px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${active === t.name ? "bg-secondary text-secondary-foreground" : "text-primary/80 hover:bg-primary/5"}`}>
              {t.label || t.name}
            </button>
          ))}
        </div>
        {current && (
          <div className="bg-card border border-primary/10 rounded-3xl p-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-9 h-9 rounded-full bg-secondary/15 text-secondary flex items-center justify-center"><Mail size={18} /></span>
              <h2 className="font-display text-2xl font-semibold text-primary">{current.label}</h2>
              <button onClick={() => setPreview(!preview)} data-testid="email-tpl-preview-toggle" className="ml-auto flex items-center gap-2 text-sm font-semibold text-primary/70 hover:text-secondary"><Eye size={15} /> {preview ? "Edit" : "Preview"}</button>
            </div>
            <div className="mb-4">
              <Label>Subject</Label>
              <Input data-testid="email-tpl-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5" />
            </div>
            <Label>Body</Label>
            {preview ? (
              <div data-testid="email-tpl-preview" className="mt-1.5 border border-primary/15 rounded-xl p-4 bg-muted/20" dangerouslySetInnerHTML={{ __html: sanitizeHtml(form.body_html) }} />
            ) : (
              <div className="mt-1.5"><RichEditor value={form.body_html} onChange={(html) => setForm({ ...form, body_html: html })} testid="email-tpl-editor" /></div>
            )}
            <p className="text-xs text-muted-foreground mt-2">Available placeholders: {(current.placeholders || []).map((p) => `{{${p}}}`).join(", ")}</p>
            <div className="flex items-center gap-4 mt-6">
              <label className="flex items-center gap-2 text-sm text-foreground/80 cursor-pointer">
                <input type="checkbox" data-testid="email-tpl-enabled" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="w-4 h-4 accent-secondary" /> Enabled
              </label>
              <Button onClick={save} disabled={saving} data-testid="email-tpl-save" className="rounded-full ml-auto">
                {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />} Save template
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
